import { useState, useMemo, useEffect } from "react";
import { useGodView } from "@/hooks/use-analytics";
import { useSession } from "@/hooks/use-auth";
import { hasRole } from "@/lib/roles";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, formatCurrency, calcDelta } from "@/lib/utils";
import { DeltaBadge } from "@/components/ui/delta-badge";
import { TrendingUp, ShoppingCart, Package, Users, Undo2, Store } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";

type Period = "7d" | "30d" | "90d" | "year";

const PERIODS: { value: Period; label: string }[] = [
  { value: "7d", label: "7 dana" },
  { value: "30d", label: "30 dana" },
  { value: "90d", label: "90 dana" },
  { value: "year", label: "Ova godina" },
];

function getDateRange(period: Period): { after: string; before: string } {
  const now = new Date();
  const before = now.toISOString();
  let after: Date;

  switch (period) {
    case "7d":
      after = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      break;
    case "30d":
      after = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      break;
    case "90d":
      after = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      break;
    case "year":
      after = new Date(now.getFullYear(), 0, 1);
      break;
  }

  return { after: after.toISOString(), before };
}

export function GodViewPage() {
  const { user } = useSession();
  const navigate = useNavigate();
  const [period, setPeriod] = useState<Period>("30d");
  const { after, before } = useMemo(() => getDateRange(period), [period]);

  const isAdmin = hasRole(user, "admin");
  const { data, isLoading, error } = useGodView(after, before, isAdmin);

  useEffect(() => {
    if (!isAdmin) {
      navigate({ to: "/dashboard" });
    }
  }, [isAdmin, navigate]);

  if (!isAdmin) return null;

  const agg = data?.aggregated;
  const prev = data?.prevAggregated;
  const stores = data?.perStore || [];

  const kpis = [
    {
      label: "Ukupan prihod",
      value: agg ? formatCurrency(agg.net_revenue) : "—",
      delta: agg && prev ? calcDelta(agg.net_revenue, prev.net_revenue) : null,
      icon: TrendingUp,
    },
    {
      label: "Narudžbe",
      value: agg ? String(agg.orders_count) : "—",
      delta: agg && prev ? calcDelta(agg.orders_count, prev.orders_count) : null,
      icon: ShoppingCart,
    },
    {
      label: "Prodanih artikala",
      value: agg ? String(agg.num_items_sold) : "—",
      delta: agg && prev ? calcDelta(agg.num_items_sold, prev.num_items_sold) : null,
      icon: Package,
    },
    {
      label: "Kupci",
      value: agg ? String(agg.total_customers) : "—",
      delta: agg && prev ? calcDelta(agg.total_customers, prev.total_customers) : null,
      icon: Users,
    },
    {
      label: "Povrati",
      value: agg ? formatCurrency(Math.abs(agg.refunds)) : "—",
      delta: agg && prev ? calcDelta(Math.abs(agg.refunds), Math.abs(prev.refunds)) : null,
      icon: Undo2,
    },
    {
      label: "Trgovine",
      value: String(stores.length),
      delta: null,
      icon: Store,
    },
  ];

  const sorted = [...stores].sort((a, b) => b.net_revenue - a.net_revenue);
  const maxRevenue = Math.max(...stores.map((s) => s.net_revenue), 1);
  const totalRevenue = stores.reduce((sum, s) => sum + s.net_revenue, 0);

  return (
    <div className="p-4 flex flex-col gap-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-sm font-medium">God View</h2>
        <div className="flex gap-0 border-b border-foreground/10 sm:border-b-0">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => setPeriod(p.value)}
              className={cn(
                "shrink-0 px-3 pb-2 pt-0.5 text-xs font-medium transition-colors relative sm:pb-0.5",
                "hover:text-foreground",
                period === p.value ? "text-foreground" : "text-muted-foreground"
              )}
            >
              {p.label}
              {period === p.value && (
                <span className="absolute bottom-0 left-3 right-3 h-px bg-foreground sm:hidden" />
              )}
            </button>
          ))}
        </div>
      </div>

      {error && <p className="text-destructive text-xs">{(error as Error).message}</p>}

      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {kpis.map((kpi) => (
            <div key={kpi.label} className="ring-1 ring-foreground/10 p-3 flex flex-col gap-1.5">
              <div className="flex items-center gap-1.5 text-muted-foreground min-w-0">
                <kpi.icon className="h-3 w-3 shrink-0" />
                <span className="text-xs truncate">{kpi.label}</span>
              </div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-sm font-medium tabular-nums">{kpi.value}</span>
                <DeltaBadge delta={kpi.delta} />
              </div>
            </div>
          ))}
        </div>
      )}

      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : sorted.length > 0 ? (
        <div className="ring-1 ring-foreground/10">
          <div className="px-3 py-2.5 border-b">
            <span className="text-xs font-medium">Po trgovinama</span>
          </div>

          <div className="p-3 flex flex-col gap-3">
            {sorted.map((store) => {
              const barPct = maxRevenue > 0 ? (store.net_revenue / maxRevenue) * 100 : 0;
              const sharePct = totalRevenue > 0 ? (store.net_revenue / totalRevenue) * 100 : 0;
              return (
                <div key={store.storeId} className="flex flex-col gap-1.5">
                  <div className="flex items-baseline justify-between gap-2 min-w-0">
                    <div className="flex items-baseline gap-2 min-w-0">
                      <span className="text-xs font-medium truncate">{store.storeName}</span>
                      <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">
                        {sharePct.toFixed(1)}%
                      </span>
                    </div>
                    <span className="text-xs font-medium tabular-nums shrink-0">
                      {formatCurrency(store.net_revenue)}
                    </span>
                  </div>
                  <div className="h-5 bg-foreground/5 w-full relative">
                    <div
                      className="h-full bg-foreground/80 transition-all duration-300"
                      style={{ width: `${Math.max(barPct, 0.5)}%` }}
                    />
                  </div>
                  <div className="flex gap-4 text-[10px] text-muted-foreground tabular-nums">
                    <span className="flex items-center gap-1">
                      <ShoppingCart className="h-2.5 w-2.5" />
                      {store.orders_count}
                    </span>
                    <span className="flex items-center gap-1">
                      <Package className="h-2.5 w-2.5" />
                      {store.num_items_sold}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="h-2.5 w-2.5" />
                      {store.total_customers}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
